import React, { useEffect } from "react";
import "./target_renewal_card.component.scss";
import Modal from "common_components/ui/modal/modal.ui";
import { Assets, FontFamily, Models, PrimaryButton } from "utils/imports.utils";
import { ButtonSize, ButtonTypes } from "utils/interface.utils";
import { getExpiryStatus, handleRenewal, useSetState } from "utils/functions.utils";

interface ITargetRenewalCard {
    isOpen: boolean;
    onClose: () => void;
    targetIds: string[];
    currentUser: any;
    isRazorpayLoaded: boolean;
}

const TargetRenewalCard = (props: ITargetRenewalCard) => {
    const [state, setState] = useSetState({
        expiryByTargetId: {} as Record<string, string | null>,
        loading: true,
        renewingTargetId: '',
    });

    const fetchExpiries = async () => {
        setState({ loading: true });
        const entries = await Promise.all(props.targetIds.map(async (targetId) => {
            try {
                const res: any = await Models.auth.resolveTarget({ target_id: targetId });
                return [targetId, res?.data?.target?.expires_at ?? res?.data?.expires_at ?? null] as const;
            } catch (error) {
                console.error('Error resolving target expiry:', error);
                return [targetId, null] as const;
            }
        }));
        setState({ expiryByTargetId: Object.fromEntries(entries), loading: false });
    }

    useEffect(() => {
        if (props.isOpen) fetchExpiries();
    }, [props.isOpen]);

    const onRenewClick = async (targetId: string) => {
        setState({ renewingTargetId: targetId });
        await handleRenewal(props.currentUser, targetId, props.isRazorpayLoaded, () => {
            setState({ renewingTargetId: '' });
            fetchExpiries();
        });
        setState({ renewingTargetId: '' });
    }

    return (
        <Modal isOpen={props.isOpen} onClose={props.onClose}>
            <div className="target-renewal-card">
                <div className="target-renewal-card-container">
                    <div className="target-renewal-card-header">
                        <div className="target-renewal-card-header-title">Target Expiry & Renewal</div>
                        <img className="target-renewal-card-header-close" src={Assets.close} alt="close" onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            props.onClose();
                        }}/>
                    </div>
                    <div className="target-renewal-card-body">
                        {state.loading && <div className="target-renewal-card-body-loading">Loading...</div>}
                        {!state.loading && props.targetIds.map((targetId) => {
                            const expiresAt = state.expiryByTargetId[targetId] ?? null;
                            const status = getExpiryStatus(expiresAt);
                            return (
                                <div className="target-renewal-card-body-row" key={targetId}>
                                    <div className="target-renewal-card-body-row-info">
                                        <div className={`target-renewal-card-body-row-info-label target-renewal-card-body-row-info-label--${status.isExpired ? 'expired' : status.isExpiringSoon ? 'soon' : 'active'}`}>
                                            {status.label}
                                            {status.daysRemaining !== null && !status.isExpired ? ` — ${status.daysRemaining}d left` : ''}
                                        </div>
                                    </div>
                                    <div className="target-renewal-card-body-row-action">
                                        {/* PrimaryButton skips its own onClick entirely when
                                            disabled, taking the e.stopPropagation() inside it with
                                            it -- so a click on a disabled Renew would bubble up to
                                            project_list's row handler and navigate to the editor.
                                            Stop it here, where it holds either way. */}
                                        <div className="target-renewal-card-body-row-action-button" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                            <PrimaryButton
                                                text="Renew"
                                                type={ButtonTypes.TERTIARY}
                                                size={ButtonSize.SMALL}
                                                fontFamily={FontFamily.medium}
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    onRenewClick(targetId);
                                                }}
                                                activity={state.renewingTargetId === targetId}
                                                // Renewal is only offered once the target is
                                                // inside the 30-day warning window getExpiryStatus
                                                // already computes -- the same threshold the
                                                // backend's d30 renewal email uses, and early
                                                // enough that a printed QR never goes dead while
                                                // the owner waits for the button to unlock.
                                                // Renewing earlier than that gains nothing: the
                                                // backend extends from the later of now and the
                                                // current expiry. A target with no expiry at all
                                                // was never paid for, and create_renewal_order
                                                // answers it with a 409, so it stays disabled too.
                                                disabled={!status.isExpired && !status.isExpiringSoon}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default TargetRenewalCard;
